// Cliente do snapshot-parser.worker — gerencia singleton + correlation
// de requests. Caller chama parseSnapshotInWorker(buffer) e recebe Promise.
//
// Singleton: 1 worker pra toda a vida do app. Worker é stateless e
// re-utilizável; spawn por request seria desperdício (cold start ~50ms
// em TVs antigas).
//
// Degradação graciosa (princípio "nenhuma leitura de diagnóstico pode
// QUEBRAR o início do app"): o worker é opcional. Se ele estiver ausente
// (sem suporte / import falhou — SecurityError síncrono no `new Worker` em
// `file://`), OU falhar EM VOO (mensagem de erro do parse, ou o evento
// `error` do worker que crasha/não-carrega — cenário real em `file://`,
// onde o fetch do script do worker pode falhar DEPOIS da construção), o
// parse cai pro main thread. Uma falha do worker NUNCA vira erro de boot
// ("Não foi possível medir" / capa/velocidade sem leitura) — só perde o
// off-thread. Por isso o buffer NÃO é transferido: o main thread retém os
// bytes pra poder reparsear. Custo: uma cópia via structured clone por load
// de snapshot (1×/24h) — barata perto do parse que ela protege.
//
// Hashes da fatia (review do `c8268b84`, P2): quem chama pode pedir o hash de
// conteúdo de algumas surfaces (`hashSurfaces`). O worker os calcula junto do
// parse, fora da thread principal. No FALLBACK (sem worker, ou worker que falhou)
// eles vêm `null` DE PROPÓSITO: o fallback não hasheia, porque na thread principal
// o hash só deve rodar depois do atalho barato do disjuntor (mesmo artefato do
// servidor), e quem sabe disso é o catalog-snapshot, não o parse.

import { decodificarJson } from './snapshot-parse-core'
import type { ParseResponse } from './snapshot-parse-core'

/** O snapshot parseado, e os hashes pedidos se o WORKER os calculou. */
export interface SnapshotParseado<T> {
  snapshot: T
  /** `null` = não foram pedidos, ou o parse caiu no fallback da thread
   *  principal (que não hasheia), ou o worker não conseguiu calcular. */
  hashes: Record<string, string> | null
}

export interface OpcoesDoParse {
  /** Surfaces cujo hash de conteúdo o worker deve devolver junto do parse. */
  hashSurfaces?: readonly string[]
}

interface PendingRequest {
  resolve(snapshot: unknown, hashes: Record<string, string> | null): void
  /** Chamado quando o worker falha (parse-error ou crash): degrada pro
   *  main thread com o buffer RETIDO, em vez de rejeitar o request em voo. */
  degrade(): void
}

let _worker: Worker | null = null
let _nextId = 0
const _pending = new Map<number, PendingRequest>()
let _initFailed = false

/** Parse síncrono no main thread — fallback quando o Worker está indisponível
 *  OU falha em voo. Lança só se o JSON for genuinamente inválido (snapshot
 *  corrompido), que é um erro real e deve propagar. NÃO hasheia (ver topo). */
function mainThreadParse<T>(buffer: ArrayBuffer): SnapshotParseado<T> {
  return { snapshot: decodificarJson(buffer) as T, hashes: null }
}

/** Hashes que o worker devolveu, se tiverem a forma certa. Qualquer outra
 *  coisa vira `null`: a thread principal recalcula se precisar. */
function hashesValidos(v: unknown): Record<string, string> | null {
  if (v === null || typeof v !== 'object') return null
  const out: Record<string, string> = {}
  for (const k of Object.keys(v)) {
    const h = (v as Record<string, unknown>)[k]
    if (typeof h !== 'string') return null
    out[k] = h
  }
  return out
}

/** Inicializa worker lazy. Idempotente. Retorna null se ambiente não
 *  suporta Worker ou import falhou (fallback main thread). */
function getWorker(): Worker | null {
  if (_initFailed) return null
  if (_worker) return _worker
  if (typeof Worker === 'undefined') {
    _initFailed = true
    return null
  }
  try {
    // import.meta.url + new URL é o pattern oficial do Vite pra workers.
    // Vite transforma isso em URL do bundle final em build.
    _worker = new Worker(new URL('./snapshot-parser.worker.ts', import.meta.url), {
      type: 'module',
    })
    _worker.addEventListener(
      'message',
      (e: MessageEvent<ParseResponse>) => {
        const { id } = e.data
        const pending = _pending.get(id)
        if (!pending) return
        _pending.delete(id)
        if (e.data.ok && e.data.snapshot !== undefined) {
          pending.resolve(e.data.snapshot, hashesValidos(e.data.hashes))
        } else {
          // Worker reportou erro de parse — degrada pro main thread (o buffer
          // foi retido, não transferido) em vez de rejeitar o request em voo.
          pending.degrade()
        }
      },
    )
    _worker.addEventListener('error', () => {
      // Worker quebrou / não carregou (comum em `file://`: o script do worker
      // pode falhar o fetch DEPOIS da construção síncrona ter passado). Degrada
      // TODOS os pendentes pro main thread e desativa o worker pras próximas.
      for (const [id, pending] of _pending) {
        _pending.delete(id)
        pending.degrade()
      }
      _initFailed = true
      _worker = null
    })
    return _worker
  } catch {
    _initFailed = true
    return null
  }
}

/**
 * Parsea o buffer do snapshot em Worker (off main thread). Fallback
 * síncrono no main thread se o Worker estiver indisponível OU falhar em voo.
 *
 * @param buffer ArrayBuffer com bytes UTF-8 do JSON. NÃO é transferido
 *   (structured clone pro worker) — o main thread retém os bytes pra
 *   fallback. Caller pode continuar usando o buffer após chamar.
 * @param opts.hashSurfaces surfaces cujo hash de conteúdo o WORKER devolve
 *   junto. No fallback vêm `null` (a thread principal não hasheia aqui).
 */
export async function parseSnapshotInWorker<T = unknown>(
  buffer: ArrayBuffer,
  opts: OpcoesDoParse = {},
): Promise<SnapshotParseado<T>> {
  const worker = getWorker()
  if (!worker) {
    // Sem Worker (ambiente sem suporte / SecurityError na construção): parse
    // direto no main thread. Degradação graciosa em vez de quebrar o login.
    return mainThreadParse<T>(buffer)
  }
  const id = _nextId++
  return new Promise<SnapshotParseado<T>>((resolve, reject) => {
    _pending.set(id, {
      resolve: (snapshot, hashes) => resolve({ snapshot: snapshot as T, hashes }),
      degrade: () => {
        try {
          resolve(mainThreadParse<T>(buffer))
        } catch (e) {
          // JSON genuinamente inválido (snapshot corrompido) — erro real,
          // propaga. Não é o caso "worker indisponível" (esse cai no fallback).
          reject(e instanceof Error ? e : new Error(String(e)))
        }
      },
    })
    // Sem lista de Transferables de propósito: o buffer NÃO é neutralizado, pra
    // sobreviver como fallback se o worker falhar/não-carregar (ver `degrade`).
    // `exactOptionalPropertyTypes`: `hashSurfaces` só entra quando há pedido.
    const pedidas = opts.hashSurfaces
    worker.postMessage(pedidas && pedidas.length > 0 ? { id, buffer, hashSurfaces: pedidas.slice() } : { id, buffer })
  })
}
